import {FormControl, FormField, TextInput} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {defaultTo} from 'ramda';
import {isNumber} from 'ramda-adjunct';

import {GetWarehouseHandlingUnitsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {logError, useGetVatRatesOptions} from '@dms/shared';
import {getOptionsFromHandlingUnits} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SupplierOrderItemForm} from '../types/SupplierOrderItemForm';

interface OrderDetailFormProps extends RequiredTestIdProps {
  formApi: UseFormReturn<SupplierOrderItemForm>;
  control: FormControl<SupplierOrderItemForm>;
  manufacturer: string | Nullish;
  handlingUnit: string | Nullish;
  handlingUnits: GetWarehouseHandlingUnitsResponse | Nullish;
  areHandlingUnitsLoading: boolean;
  currency: string;
}

export function OrderDetailForm(props: OrderDetailFormProps) {
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const handleSupplierUnitChange = (value: string | number | null) => {
    if (!value) {
      return logError('SupplierUnit missing value');
    }

    if (isNumber(value)) {
      return logError('SupplierUnit value should be string');
    }

    props.formApi.setValue('supplierArticle.supplierUnitId', value);
  };

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.orderDetail')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="orderDetail.supplierQuantity"
            label={i18n.t('entity.warehouse.labels.supplierQuantity')}
            decimalPlaces={4}
            suffix={props.handlingUnit}
            data-testid={suffixTestId('inputs.supplierQuantity', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isNotClearable
            control={props.control}
            type="choice"
            name="orderDetail.supplierUnit"
            onChange={handleSupplierUnitChange}
            label={i18n.t('entity.warehouse.labels.supplierUnit')}
            options={getOptionsFromHandlingUnits(props.handlingUnits)}
            isLoading={props.areHandlingUnitsLoading}
            data-testid={suffixTestId('inputs.supplierUnit', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="orderDetail.supplierUnitPrice"
            label={i18n.t('general.labels.unitPrice')}
            decimalPlaces={2}
            suffix={props.currency}
            data-testid={suffixTestId('inputs.unitPrice', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="orderDetail.supplierTotalPrice"
            label={i18n.t('general.labels.totalPrice')}
            decimalPlaces={2}
            suffix={props.currency}
            isDisabled
            data-testid={suffixTestId('inputs.totalPrice', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.catalogueNumber"
            label={i18n.t('entity.warehouse.labels.catalogueNumber')}
            isDisabled
            data-testid={suffixTestId('inputs.catalogueNumber', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <TextInput
            value={defaultTo('', props.manufacturer)}
            label={i18n.t('entity.warehouse.labels.manufacturer')}
            isDisabled
            data-testid={suffixTestId('inputs.manufacturerId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.name"
            label={i18n.t('entity.warehouse.labels.name')}
            isDisabled
            data-testid={suffixTestId('inputs.name', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.supplierOrderingNumber"
            label={i18n.t('entity.warehouse.labels.supplierOrderingNumber')}
            data-testid={suffixTestId('inputs.supplierOrderingNumber', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4} align="center">
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.vin"
            label={i18n.t('entity.warehouse.labels.vin')}
            data-testid={suffixTestId('inputs.vin', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.keyCode"
            label={i18n.t('entity.warehouse.labels.keyCode')}
            data-testid={suffixTestId('inputs.keyCode', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.engineNumber"
            label={i18n.t('entity.warehouse.labels.engineNumber')}
            data-testid={suffixTestId('inputs.engineNumber', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.engineCode"
            label={i18n.t('entity.warehouse.labels.engineCode')}
            data-testid={suffixTestId('inputs.engineCode', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4} align="center">
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.location"
            label={i18n.t('entity.warehouse.labels.location')}
            data-testid={suffixTestId('inputs.location', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isDisabled
            control={props.control}
            type="text"
            name="orderDetail.referenceNumber"
            label={i18n.t('entity.warehouse.labels.externalOrderItemRefNo')}
            data-testid={suffixTestId('inputs.externalOrderItemRefNo', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="text"
            name="orderDetail.labelInfo"
            label={i18n.t('entity.warehouse.labels.labelInfo')}
            data-testid={suffixTestId('inputs.labelInfo', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="choice"
            name="orderDetail.vat"
            label={i18n.t('entity.warehouse.labels.vat')}
            options={vatOptions}
            data-testid={suffixTestId('inputs.vat', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4} align="center">
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="integer"
            name="orderDetail.mileage"
            label={i18n.t('entity.vehicle.labels.mileage')}
            minStepperValue={0}
            suffix={i18n.t('general.metric.km')}
            data-testid={suffixTestId('inputs.mileage', props)}
          />
        </GridItem>
        <GridItem span={3}>
          <FormField
            control={props.control}
            type="textarea"
            name="orderDetail.note"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.note', props)}
          />
        </GridItem>
      </Grid>

      <FormField
        control={props.control}
        type="switch"
        name="orderDetail.isIntelligentPart"
        label={i18n.t('entity.warehouse.labels.isIntelligentPart')}
        data-testid={suffixTestId('inputs.isIntelligentPart', props)}
      />
    </VStack>
  );
}
